'use client'

import { useEffect, useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'
import { object, ref, string } from 'yup'

import Button from 'shared/ui/Button'
import Input from 'shared/ui/form/Input'
import toast from 'shared/ui/utils/toast'
import { validateDateOfBirth } from 'shared/utils/date'
import { getApiErrors } from 'shared/utils/errors'
import { validateField, validateValues } from 'shared/utils/form'
import { isNilOrEmpty } from 'shared/utils/ramda'

import { validateMaxValueDateOfBirth } from '../age'
import { updateCustomerDetails } from '../api'
import {
  extractCustomerFromClientSession,
  extractTokenFromClientSession} from '../session'

import styles from './OnboardCustomerForm.module.scss'

const defaultValues = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  phoneNumber: ''
}

interface OnboardCustomerFormValues {
  firstName: string
  lastName: string
  dateOfBirth: string
  phoneNumber: string
}

const schema = object().shape({
  firstName: string()
    .required('Imię jest wymagane')
    .min(3, 'Imię zbyt krótkie')
    .matches(/^\D*$/, {
      message: 'Nieprawidłowy format imienia'
    }),
  lastName: string()
    .required('Nazwisko jest wymagane')
    .min(3, 'Nazwisko zbyt krótkie')
    .matches(/^[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]+(-[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]+)*$/, {
      message: 'Nieprawidłowy format nazwiska'
    }),
  phoneNumber: string().test(
    'is-present-or-valid',
    'Numer telefonu jest wymagany lub ma nieprawidłowy format',
    function (value) {
      const { path, createError } = this
      const phoneNumber = this.resolve(ref(path))

      if (isNilOrEmpty(phoneNumber) && isNilOrEmpty(value)) {
        return createError({ path, message: 'Numer telefonu jest wymagany' })
      }

      if (value && !/^\d{9}$/.test(value)) {
        return createError({ path, message: 'Nieprawidłowy format' })
      }

      return true
    }
  ),
  dateOfBirth: string()
    .test(
      'is-present-or-valid',
      'Data urodzenia wygląda na nieprawidłową',
      validateDateOfBirth
    )
    .test(
      'is-older-than-13-years-or-valid',
      'Aby korzystać z naszych usług, musisz mieć ukończone 13 lat',
      validateMaxValueDateOfBirth
    )
    .required('Data jest wymagana')
})

const maxDateInput = new Date().toISOString().split('T')[0]

const validateFormField = (values: OnboardCustomerFormValues) =>
  validateField(schema, values)
const validateFormValues = validateValues(schema)

const OnboardCustomerForm = () => {
  const [values, setValues] = useState<OnboardCustomerFormValues>(defaultValues)
  const [valid, setValid] = useState(false)
  const session = useSession()
  const { update, data } = session
  const user = extractCustomerFromClientSession(session)
  const token = extractTokenFromClientSession(session)
  const [loading, setLoading] = useState(false)

  // Memoize the validation function to prevent infinite re-renders
  const memoizedValidateFormField = useMemo(() => {
    return validateFormField(values)
  }, [values])

  const handleValueChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  useEffect(() => {
    validateFormValues(values, setValid)
  }, [values])

  useEffect(() => {
    if (user) {
      setValues({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        dateOfBirth: user?.dateOfBirth || '',
        phoneNumber: user?.phoneNumber || ''
      })
    }
  }, [user])

  const handleSubmit = async () => {
    setLoading(true)
    await updateCustomerDetails({ token, values })
      .then(async resp => {
        const customer = resp.data.data

        if (data) {
          await update({
            ...data,
            user: {
              ...data.user,
              customer
            }
          })
        }

        setLoading(false)
        toast.success('Pomyślnie zapisano zmiany')
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
        toast.error(getApiErrors(err))
      })
  }

  return (
    <div className={styles.wrapper}>
      <Input
        id='onboard_first_name'
        required
        name='firstName'
        label='Imię'
        value={values.firstName}
        onChange={handleValueChange}
        validate={memoizedValidateFormField}
      />
      <Input
        id='onboard_last_name'
        required
        name='lastName'
        label='Nazwisko'
        value={values.lastName}
        onChange={handleValueChange}
        validate={memoizedValidateFormField}
      />
      <Input
        id='onboard_date_of_birth'
        required
        name='dateOfBirth'
        label='Data urodzenia'
        type='date'
        value={values.dateOfBirth}
        onChange={handleValueChange}
        validate={memoizedValidateFormField}
        inputProps={{
          max: maxDateInput
        }}
      />
      <Input
        id='onboard_phone_number'
        required
        name='phoneNumber'
        icons={{
          start: <div>+48</div>
        }}
        label='Numer telefonu'
        value={values.phoneNumber}
        onChange={handleValueChange}
        validate={memoizedValidateFormField}
      />
      <div className={styles.buttonsWrapper}>
        <Button
          disabled={!valid || loading}
          loading={loading}
          variant='primary'
          fullWidth
          size='large'
          onClick={handleSubmit}
        >
          Zapisz
        </Button>
      </div>
    </div>
  )
}

export default OnboardCustomerForm
